const WORKSPACE_ROUTE_STACK_KEY = 'testsuite_workspace_route_stack';
const MAX_WORKSPACE_ROUTE_STACK_LENGTH = 40;

const getSessionStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_err) {
    return null;
  }
};

const normalizeRoute = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized || !normalized.startsWith('/')) return '';
  return normalized;
};

const routePathname = (value = '') => {
  const normalized = normalizeRoute(value);
  if (!normalized) return '';
  return normalized.split(/[?#]/)[0] || '/';
};

const normalizeRoutePrefix = (value = '') => {
  const pathname = routePathname(value);
  if (!pathname || pathname === '/') return '';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

const matchesRoutePrefix = (route, prefix) => {
  const pathname = routePathname(route);
  const normalizedPrefix = normalizeRoutePrefix(prefix);
  if (!pathname || !normalizedPrefix) return false;
  return pathname === normalizedPrefix || pathname.startsWith(`${normalizedPrefix}/`);
};

const isTrackableRoute = (value = '') => {
  const pathname = routePathname(value);
  if (!pathname || pathname === '/' || pathname.startsWith('/login') || pathname.startsWith('/app')) {
    return false;
  }
  return true;
};

const readRouteStack = () => {
  const storage = getSessionStorage();
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(WORKSPACE_ROUTE_STACK_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.map(normalizeRoute).filter(isTrackableRoute)
      : [];
  } catch (_err) {
    return [];
  }
};

const writeRouteStack = (stack = []) => {
  const storage = getSessionStorage();
  if (!storage) return;
  const normalizedStack = stack.map(normalizeRoute).filter(isTrackableRoute);
  if (!normalizedStack.length) {
    storage.removeItem(WORKSPACE_ROUTE_STACK_KEY);
    return;
  }
  storage.setItem(
    WORKSPACE_ROUTE_STACK_KEY,
    JSON.stringify(normalizedStack.slice(-MAX_WORKSPACE_ROUTE_STACK_LENGTH))
  );
};

export const recordWorkspaceRoute = (route) => {
  const normalizedRoute = normalizeRoute(route);
  if (!isTrackableRoute(normalizedRoute)) return;

  const stack = readRouteStack();
  if (stack[stack.length - 1] === normalizedRoute) return;
  stack.push(normalizedRoute);
  writeRouteStack(stack);
};

export const consumeWorkspaceBackRoute = (currentRoute, {
  skipRoutes = [],
  allowedRoutePrefixes = [],
  ignoreStoredStack = false,
} = {}) => {
  if (ignoreStoredStack) return '';

  const normalizedCurrent = normalizeRoute(currentRoute);
  const skipped = new Set(skipRoutes.map(normalizeRoute).filter(Boolean));
  const skippedPathnames = new Set(skipRoutes.map(routePathname).filter(Boolean));
  const allowedPrefixes = allowedRoutePrefixes
    .map(normalizeRoutePrefix)
    .filter(Boolean);
  const stack = readRouteStack();

  while (stack.length && stack[stack.length - 1] === normalizedCurrent) {
    stack.pop();
  }

  while (stack.length) {
    const candidate = stack.pop();
    const candidatePathname = routePathname(candidate);
    if (!candidate || candidate === normalizedCurrent) continue;
    if (skipped.has(candidate) || skippedPathnames.has(candidatePathname)) continue;
    if (!isTrackableRoute(candidate)) continue;
    if (
      allowedPrefixes.length > 0
      && !allowedPrefixes.some((prefix) => matchesRoutePrefix(candidate, prefix))
    ) {
      continue;
    }

    writeRouteStack([...stack, candidate]);
    return candidate;
  }

  writeRouteStack([]);
  return '';
};

export const wasCurrentDocumentReloaded = () => {
  try {
    const navigationEntries = typeof window !== 'undefined'
      && window.performance
      && typeof window.performance.getEntriesByType === 'function'
      ? window.performance.getEntriesByType('navigation')
      : [];
    const latestNavigation = Array.isArray(navigationEntries) ? navigationEntries[0] : null;
    if (latestNavigation?.type) {
      return latestNavigation.type === 'reload';
    }

    return Boolean(
      typeof window !== 'undefined'
      && window.performance
      && window.performance.navigation
      && window.performance.navigation.type === 1
    );
  } catch (_err) {
    return false;
  }
};
